import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { patch } from "@/lib/api";
import { parseNumber } from "@/lib/format";
import type { UpdateOutput } from "@/lib/types";
import type { Customer } from "./shared";

export function CustomerEditDialog({ customer, onClose }: { customer: Customer | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [rate, setRate] = useState("");
  useEffect(() => {
    setName(customer?.name ?? "");
    setRate(customer?.defaultRate == null ? "" : String(customer.defaultRate).replace(".", ","));
  }, [customer]);

  const rateValue = parseNumber(rate);
  const valid = name.trim() !== "" && (rate.trim() === "" || (rateValue !== null && rateValue >= 0));
  const save = useMutation({
    mutationFn: () => patch<UpdateOutput>(`/api/customers/${customer!.id}`, { name: name.trim(), defaultRate: rate.trim() === "" ? null : rateValue }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["company"] });
      toast.success("Kunde gespeichert");
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open={!!customer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Kunde bearbeiten</DialogTitle></DialogHeader>
        <form className="contents" onSubmit={(e) => { e.preventDefault(); if (valid && !save.isPending) save.mutate(); }}>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label htmlFor="en">Name</Label><Input id="en" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div className="space-y-1.5"><Label htmlFor="er">Stundensatz (leer = Standard)</Label><Input id="er" inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} /></div>
          <p className="text-xs text-muted-foreground">Nur lokal — der sevDesk-Kontakt bleibt unverändert.</p>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button disabled={!valid || save.isPending} type="submit">Speichern</Button>
        </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
